import classNames from 'classnames';
import React, { useEffect, useRef, useState } from 'react';
import { Button, Tabs } from '@meitu/saas-ui';
import { convertWebpToImage, ownCloudImage } from '@meitu/saas-editor-core/es/utils/file-kit';
import { getImageBaseMask, retouching } from '@meitu/x-design-ai-api';
import IconFont from '@meitu/xdesign-icon-font';
import { useLocaleStore } from '@/i18n';
import styles from './index.module.less';

export type ProductTypeValue = 'general' | 'hardware' | 'cosmetics' | 'leather';

export type ProductType = {
    label: string;
    value: ProductTypeValue;
    icon: string;
    showVipIcon: boolean;
};
type Props = {
    productTypes: ProductType[];
    imageURL: string;
    commercialStrategy?: 'editor_ai_goods_fix' | 'commerce_ai_goods_fix';
    transfer?: string;
    vipIcon?: string;
    onBeforeApply: (activeType: ProductTypeValue) => Promise<boolean>;
    onAfterApply: (url: string, activeType: ProductTypeValue) => void;
    onApplyFailed?: () => void;
    onApplyFinally?: () => void;
    onTypeChange?: (activeType: ProductTypeValue) => void;
    onMattingChange?: (isMatting: boolean) => void;
    fromDemo?: boolean;
    mattingType?: 0 | 1 | 2; // 0不抠图， 1抠图，2选择

    // 商业化核销携带参数
    v2Params?: Record<string, any>;
};

export const ProductRetouchingPanel: React.FC<Props> = ({
    productTypes,
    imageURL,
    commercialStrategy = 'editor_ai_goods_fix',
    transfer,
    vipIcon,
    onBeforeApply,
    onAfterApply,
    onApplyFailed,
    onApplyFinally,
    onTypeChange,
    onMattingChange,
    fromDemo,
    mattingType = 2,
    v2Params,
}) => {
    const { $t } = useLocaleStore();
    const [loading, setLoading] = useState(false);
    const [activeType, setActiveType] = useState<ProductTypeValue>('general');
    const maskURL = useRef('');
    const [isMatting, setIsMatting] = useState<boolean>(true);

    imageURL = ownCloudImage(imageURL) ? convertWebpToImage(imageURL) : imageURL;

    useEffect(() => {
        if (mattingType !== 2) {
            setIsMatting(mattingType === 1);
        }
    }, []);

    const getMattingResult = async (url: string) => {
        const { maskURL: result } = await getImageBaseMask({
            isUseNewAPI: true,
            params: {
                imageURL: url,
                keepResolution: false,
                fromDemo,
            },
        });
        maskURL.current = result;
    };

    const handleApply = async () => {
        const flag = await onBeforeApply(activeType);
        if (!flag) return;

        setLoading(true);
        if (isMatting && !maskURL.current) {
            await getMattingResult(imageURL);
        }

        try {
            if (!maskURL.current && isMatting) throw '';
            const resultURL = await retouching({
                originalImageURL: imageURL,
                maskURL: isMatting ? maskURL.current : undefined,
                subfunc: commercialStrategy,
                transfer: transfer,
                fromDemo,
                v2Params,
            });
            onAfterApply(resultURL, activeType);
        } catch (error) {
            console.log(error);
            onApplyFailed?.();
        } finally {
            onApplyFinally?.();
            setLoading(false);
        }
    };

    const handleTypeClick = (value: ProductTypeValue) => {
        setActiveType(value);
        onTypeChange?.(value);
    };

    const handleMattingClick = (key: string) => {
        setIsMatting(key === 'matting');
        onMattingChange?.(key === 'matting');
    };

    return (
        <div className={styles.panel}>
            {mattingType === 2 ? (
                <div className={styles['matting-container']}>
                    <Tabs
                        data={[
                            { name: $t((v) => v.productRetouching.matting), tabKey: 'matting' },
                            { name: $t((v) => v.productRetouching.fullImage), tabKey: 'fullImage' },
                        ]}
                        handleTabClick={handleMattingClick}
                        tabsHeight="34px"
                    />
                </div>
            ) : null}

            <div className={styles.types}>
                <div className={styles.title}>{$t((v) => v.productRetouching.select)}</div>
                {productTypes.map((p) => {
                    return (
                        <div
                            className={classNames(styles.type, activeType === p.value ? styles.active : null)}
                            key={p.value}
                            onClick={() => handleTypeClick(p.value)}
                        >
                            <div className={styles.wrap}>
                                <IconFont fontSize={24} name={p.icon} />
                                <div className={styles.name}>{p.label}</div>
                            </div>
                            {p.showVipIcon && vipIcon && <img src={vipIcon} alt="svip" className={styles.vip} />}
                        </div>
                    );
                })}
                <div></div>
            </div>

            <Button className={styles.apply} type="primary" onClick={handleApply}>
                {loading ? $t((v) => v.productRetouching.applying) : $t((v) => v.productRetouching.apply)}
            </Button>
        </div>
    );
};
